<script setup lang="ts">
  import { ref } from 'vue';
  import type { SwiperContainer } from 'swiper/element';
  import type { StarterBlockData } from '~/models/chat';
  import { ChatState } from '~/models/chat';

  defineProps({
    starterBlocks: {
      type: Array as PropType<StarterBlockData[]>,
      required: true
    },
    canWrite: {
      type: Boolean,
      required: true
    }
  });

  const emit = defineEmits(['send']);

  const chatStore = useChatStore();
  const { chatState } = storeToRefs(chatStore);

  const isInitialized = ref(false);
  const swiperRef = ref<SwiperContainer | null>(null);

  const send = async (message: string) => {
    emit('send', message);
    chatState.value = ChatState.Chat;
  };

  const scrollRef = ref<HTMLElement | null>(null);

  const hasScrollbar = () => {
    if (!scrollRef.value) return false;

    const scrollTop = scrollRef.value.scrollTop;
    if (scrollTop > 0) return true;

    scrollRef.value.scrollTop += 10;
    if (scrollTop === scrollRef.value.scrollTop) return false;

    scrollRef.value.scrollTop = scrollTop;
    return true;
  };

  onMounted(() => {
    swiperRef.value?.initialize();
    isInitialized.value = true;
  });
</script>

<template>
  <div
    ref="scrollRef"
    :class="[
      'chat-screen',
      {
        'chat-screen--has-scrollbar': hasScrollbar()
      },
      'chat-home-screen'
    ]">
    <div class="chat-screen__wrapper">
      <div class="chat-home-screen__heading">
        <h2>{{ $t('chat.home.title') }}</h2>
        <p>
          {{ $t('chat.home.subtitle') }}
        </p>
      </div>
      <div class="chat-home-screen__blocks">
        <swiper-container
          ref="swiperRef"
          :class="[{ 'swiper-initialized': isInitialized }]"
          slides-per-view="auto"
          :space-between="24"
          :centered-slides="true"
          :rewind="false"
          :loop="false"
          :speed="800"
          :init="false"
          :breakpoints="{ 1023: { centeredSlides: false } }">
          <swiper-slide
            v-for="(block, index) in starterBlocks"
            :key="index">
            <ChatStarterBlock
              v-bind="block"
              @send="send" />
          </swiper-slide>
        </swiper-container>
      </div>
      <div class="chat-home-screen__input">
        <ChatInput
          :can-write="canWrite"
          @send="send" />
      </div>
    </div>
  </div>
</template>
